import type { AnnualRate, RiskProfile } from './constants';
import type { FutureSavingsPlannerForm, YearProjection } from './types';

import { dayjsDate, getAge } from '@polyseme/utils/date/date-utils';
import { ObjectUtils } from '@polyseme/utils/typescript/typescript-utils';

import { ANNUAL_RATES } from './constants';

/**
 * Gets the annual interest rate based on the user's risk profile.
 * @param riskProfile The user's risk profile.
 * @returns The corresponding annual interest rate.
 */
export const getRateByRiskProfile = (riskProfile: RiskProfile): AnnualRate => {
  return ANNUAL_RATES[riskProfile];
};

/**
 * Calculates the equivalent monthly interest rate from an annual rate.
 * @param annualRate The annual interest rate (e.g., 0.05 for 5%).
 * @returns The monthly interest rate.
 */
const calculateMonthlyRate = (annualRate: number): number => {
  if (annualRate <= -1) {
    throw new Error(
      'Annual rate must be greater than -1 to calculate monthly rate.'
    );
  }
  return Math.pow(1 + annualRate, 1 / 12) - 1;
};

/**
 * Determines the target year for retirement projection.
 * This is the year the user turns the retirement age.
 * @param birthYear The user's birth year.
 * @param retirementAge The user's target retirement age.
 * @param currentYear The current year.
 * @returns The year the projection should end (inclusive).
 */
const getRetirementYear = (
  birthYear: number,
  retirementAge: number,
  currentYear: number
): number => {
  const yearUserTurnsRetirementAge = birthYear + retirementAge;
  return Math.max(currentYear, yearUserTurnsRetirementAge);
};

/**
 * Calculates the number of months remaining in the current year from a given start month.
 * @param startMonth The 1-indexed month to start from.
 * @returns The number of months remaining.
 */
const getMonthsRemainingInYear = (startMonth: number): number => {
  return 12 - startMonth + 1;
};

const simulateMonthlyCompoundingForYear = ({
  currentBalance,
  monthlyPayment,
  monthlyRate,
  startMonth,
}: {
  currentBalance: number;
  monthlyPayment: number;
  monthlyRate: number;
  startMonth: number;
}): number => {
  let balance = currentBalance;
  for (let month = startMonth; month <= 12; month++) {
    balance = balance * (1 + monthlyRate) + monthlyPayment;
  }
  return balance;
};

/**
 * Simulates yearly projections of savings until retirement.
 * Supports an optional start delay before contributions begin.
 */
export const simulateYearlyProjections = ({
  birthDate,
  initialDeposit,
  monthlyPayment,
  rate,
  retirementAge: _retirementAge,
  startDelay = 0,
}: FutureSavingsPlannerForm & {
  rate: AnnualRate;
  startDelay?: number;
}) => {
  const projections: YearProjection[] = [];
  const retirementAge = +_retirementAge;

  const now = dayjsDate();
  const currentYear = now.year();
  const currentMonth = now.month() + 1;

  const birthDayjs = dayjsDate(birthDate, 'YYYY-MM-DD', true);
  const birthYear = birthDayjs.year();

  const actualCurrentAge = getAge(birthDate);
  if (retirementAge < actualCurrentAge) return { projections: [] };

  const endProjectionYear = getRetirementYear(
    birthYear,
    retirementAge,
    currentYear
  );

  let medianBalance = initialDeposit;
  let pessimisticBalance = initialDeposit;
  let optimisticBalance = initialDeposit;
  let investmentMade = initialDeposit;

  const monthlyMedianRate = calculateMonthlyRate(rate.median);
  const monthlyPessimisticRate = calculateMonthlyRate(rate.min);
  const monthlyOptimisticRate = calculateMonthlyRate(rate.max);

  for (let year = currentYear; year <= endProjectionYear; year++) {
    let startMonthForYearlyCalc: number;
    let monthlyPaymentsForThisPeriod: number;

    if (year === currentYear) {
      startMonthForYearlyCalc = currentMonth;
    } else {
      startMonthForYearlyCalc = 1;
    }

    // If we are still in the delay period, no contributions
    const yearsSinceStart = year - currentYear;
    const effectiveMonthlyPayment =
      yearsSinceStart < startDelay ? 0 : monthlyPayment;

    if (year === currentYear) {
      monthlyPaymentsForThisPeriod =
        getMonthsRemainingInYear(currentMonth) * effectiveMonthlyPayment;
    } else {
      monthlyPaymentsForThisPeriod = 12 * effectiveMonthlyPayment;
    }

    investmentMade += monthlyPaymentsForThisPeriod;

    medianBalance = simulateMonthlyCompoundingForYear({
      currentBalance: medianBalance,
      monthlyPayment: effectiveMonthlyPayment,
      monthlyRate: monthlyMedianRate,
      startMonth: startMonthForYearlyCalc,
    });
    pessimisticBalance = simulateMonthlyCompoundingForYear({
      currentBalance: pessimisticBalance,
      monthlyPayment: effectiveMonthlyPayment,
      monthlyRate: monthlyPessimisticRate,
      startMonth: startMonthForYearlyCalc,
    });
    optimisticBalance = simulateMonthlyCompoundingForYear({
      currentBalance: optimisticBalance,
      monthlyPayment: effectiveMonthlyPayment,
      monthlyRate: monthlyOptimisticRate,
      startMonth: startMonthForYearlyCalc,
    });

    projections.push({
      year,
      investmentMade: parseFloat(investmentMade.toFixed(2)),
      median: parseFloat(medianBalance.toFixed(2)),
      pessimistic: parseFloat(pessimisticBalance.toFixed(2)),
      optimistic: parseFloat(optimisticBalance.toFixed(2)),
    });
  }

  return { projections };
};

/**
 * Compute projections for all risk profiles.
 */
export const getAllProjections = (
  form: Omit<FutureSavingsPlannerForm, 'riskProfile'>
): Record<RiskProfile, YearProjection[]> => {
  return ObjectUtils.keys(ANNUAL_RATES).reduce(
    (acc, profile) => {
      acc[profile] = simulateYearlyProjections({
        ...form,
        riskProfile: profile,
        rate: ANNUAL_RATES[profile],
      }).projections;
      return acc;
    },
    {} as Record<RiskProfile, YearProjection[]>
  );
};

/**
 * Compute the maximum amount across all projections
 */
export const getMaxAmount = (
  allProjections: Record<RiskProfile, YearProjection[]>
): number => {
  return Math.max(
    ...ObjectUtils.keys(allProjections).map((profile) => {
      const projections = allProjections[profile];
      if (!projections.length) return 0;
      const last = projections[projections.length - 1];
      if (!last) return 0;
      return Math.max(
        last.optimistic,
        last.median,
        last.pessimistic,
        last.investmentMade
      );
    })
  );
};
